import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GlobalEmptyState from '../components/GlobalEmptyState';
import GlobalSidebar from '../components/GlobalSidebar';
import { GlobalSortableTh } from '../components/GlobalSortableTh';
import { GlobalButton } from '../components/GlobalButton';
import {
  RealEstatePropertyForm,
  type RealEstatePropertyFormValues,
} from '../components/RealEstatePropertyForm';
import { StatCard } from '../components/StatCard';
import { SortableStatGrid } from '../components/SortableStatGrid';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { ViewLayoutToggle } from '../components/ViewLayoutToggle';
import { ViewSections, type ViewSection } from '../components/ViewSections';
import { useTableSort } from '../hooks/useTableSort';
import { useViewLayoutMode } from '../hooks/useViewLayoutMode';
import { useAppData } from '../context/AppDataContext';
import { useTheme } from '../context/ThemeProvider';
import { useUser } from '../context/UserContext';
import { saveRealEstateProperty } from '../lib/saveRealEstateProperty';
import { propertyEquity, realEstateTypeLabel } from '../lib/realEstate';
import {
  totalRealEstateDebt,
  totalRealEstateEquity,
  totalRealEstateValue,
} from '../utils/calculations';
import { buildRealEstateKpiExplains } from '../lib/kpiExplainBuilders';
import { formatEuro } from '../utils/formatters';
import { Favicon } from '../utils/favicon';
import authStyles from '../pages/auth/authPages.module.css';
import styles from './RealEstateView.module.css';

function valueClassName(value: number): string {
  if (value > 0) return 'stat-value--wrap financialSnapshot-profit financialSnapshot-profit--positive';
  if (value < 0) return 'stat-value--wrap financialSnapshot-profit financialSnapshot-profit--negative';
  return 'stat-value--wrap';
}

function equityClassName(equity: number): string {
  if (equity > 0) return 'financialSnapshot-profit financialSnapshot-profit--positive';
  if (equity < 0) return 'financialSnapshot-profit financialSnapshot-profit--negative';
  return '';
}

type PropertySortKey = 'name' | 'type' | 'address' | 'value' | 'equity' | 'mortgage' | 'rent';

export function RealEstateView() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { mode: layoutMode, toggle: toggleLayout } = useViewLayoutMode();
  const { realEstateItems, realEstateLoading } = useAppData();
  const [addSidebarOpen, setAddSidebarOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const properties = useMemo(() => realEstateItems ?? [], [realEstateItems]);
  const hasProperties = properties.length > 0;

  const { sortKey, sortDirection, toggleSort, sortRows } = useTableSort<PropertySortKey>(
    'value',
    'desc',
  );

  const sortedProperties = useMemo(() => {
    return sortRows(properties, {
      name: (property) => property.name,
      type: (property) => realEstateTypeLabel(property.propertyType),
      address: (property) => property.address?.trim() || '',
      value: (property) => (Number.isFinite(property.currentValue) ? property.currentValue : 0),
      equity: (property) => propertyEquity(property),
      mortgage: (property) =>
        typeof property.mortgageBalance === 'number' && Number.isFinite(property.mortgageBalance)
          ? property.mortgageBalance
          : 0,
      rent: (property) =>
        typeof property.monthlyRent === 'number' && Number.isFinite(property.monthlyRent)
          ? property.monthlyRent
          : 0,
    });
  }, [properties, sortRows]);

  const summary = useMemo(() => {
    const list = realEstateItems ?? [];
    const totalValue = totalRealEstateValue(list);
    const equity = totalRealEstateEquity(list);
    const debt = totalRealEstateDebt(list);
    const monthlyRent = list.reduce(
      (sum, property) =>
        sum +
        (typeof property.monthlyRent === 'number' && Number.isFinite(property.monthlyRent)
          ? property.monthlyRent
          : 0),
      0
    );
    return { totalValue, equity, debt, monthlyRent, count: list.length };
  }, [realEstateItems]);

  const kpiExplains = useMemo(
    () => buildRealEstateKpiExplains(properties, summary),
    [properties, summary],
  );

  const openAddSidebar = useCallback(() => {
    setSaveError(null);
    setAddSidebarOpen(true);
  }, []);

  const closeAddSidebar = useCallback(() => {
    if (saving) return;
    setAddSidebarOpen(false);
    setSaveError(null);
  }, [saving]);

  async function handleSave(values: RealEstatePropertyFormValues) {
    if (!user?.uid || saving) return;

    setSaveError(null);
    setSaving(true);

    try {
      await saveRealEstateProperty(user.uid, values);
      setAddSidebarOpen(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save property.');
    } finally {
      setSaving(false);
    }
  }

  const plusIcon = (
    <img
      src={isDarkMode ? '/assets/icons/plus.circle.svg' : '/assets/icons/plus.circle.black.svg'}
      alt=""
      width={16}
      height={16}
    />
  );

  const toolbarActions = (
    <>
      <ViewLayoutToggle mode={layoutMode} onToggle={toggleLayout} />
      <GlobalButton
        type="button"
        variant="secondary"
        className={styles.tapTarget}
        disabled={saving}
        onClick={openAddSidebar}
        icon={plusIcon}
      >
        Add property
      </GlobalButton>
    </>
  );

  const summarySection =
    !realEstateLoading && hasProperties ? (
      <>
        <ViewSectionHeader
          eyebrow="Real estate"
          title="Summary"
          subtitle="Combined value and equity across your real-estate holdings."
        />
        <SortableStatGrid
          viewId="real-estate"
          className="grid-stats grid-stats-2"
          ariaLabel="Real estate summary"
          cards={[
            {
              id: 'net-equity',
              pinned: true,
              label: 'Net equity',
              node: (
                <StatCard
                  netWorthHeadline
                  label="Net equity"
                  emoji="🏠"
                  value={<span className={valueClassName(summary.equity)}>{formatEuro(summary.equity)}</span>}
                  hint={`Across ${summary.count} ${summary.count === 1 ? 'property' : 'properties'}`}
                  kpiExplain={kpiExplains['net-equity']}
                />
              ),
            },
            {
              id: 'total-value',
              label: 'Total value',
              node: (
                <StatCard
                  label="Total value"
                  emoji="📊"
                  value={formatEuro(summary.totalValue)}
                  hint="Combined market value"
                  valueWrap
                  kpiExplain={kpiExplains['total-value']}
                />
              ),
            },
            {
              id: 'mortgages',
              label: 'Mortgages',
              node: (
                <StatCard
                  label="Mortgages"
                  emoji="📉"
                  value={formatEuro(summary.debt)}
                  hint="Outstanding loan balance"
                  valueWrap
                  kpiExplain={kpiExplains.mortgages}
                />
              ),
            },
            {
              id: 'monthly-rent',
              label: 'Monthly rent',
              node: (
                <StatCard
                  label="Monthly rent"
                  emoji="💶"
                  value={formatEuro(summary.monthlyRent)}
                  hint="Gross rental income"
                  valueWrap
                  kpiExplain={kpiExplains['monthly-rent']}
                />
              ),
            },
          ]}
        />
      </>
    ) : null;

  const propertiesSection = (
    <>
      <ViewSectionHeader
        eyebrow="Real estate"
        title="Properties"
        subtitle="Track each property's market value, mortgage, and rental income."
      />

      <section className={styles.root}>
        {saveError ? (
          <div
            className={`${authStyles.banner} ${authStyles.bannerError} ${styles.banner}`}
            role="alert"
          >
            {saveError}
          </div>
        ) : null}

        {realEstateLoading ? (
          <p className="muted">Loading real estate…</p>
        ) : hasProperties ? (
          <div className="table-card">
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <GlobalSortableTh sortKey="name" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                      Property
                    </GlobalSortableTh>
                    <GlobalSortableTh sortKey="type" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                      Type
                    </GlobalSortableTh>
                    <GlobalSortableTh sortKey="address" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                      Address
                    </GlobalSortableTh>
                    <GlobalSortableTh sortKey="value" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                      Value
                    </GlobalSortableTh>
                    <GlobalSortableTh sortKey="equity" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                      Equity
                    </GlobalSortableTh>
                    <GlobalSortableTh sortKey="mortgage" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                      Mortgage
                    </GlobalSortableTh>
                    <GlobalSortableTh sortKey="rent" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                      Rent / mo
                    </GlobalSortableTh>
                  </tr>
                </thead>
                <tbody>
                  {sortedProperties.map((property) => {
                    const equity = propertyEquity(property);
                    const mortgage =
                      typeof property.mortgageBalance === 'number' &&
                      Number.isFinite(property.mortgageBalance) &&
                      property.mortgageBalance > 0
                        ? property.mortgageBalance
                        : null;
                    const rent =
                      typeof property.monthlyRent === 'number' &&
                      Number.isFinite(property.monthlyRent) &&
                      property.monthlyRent > 0
                        ? property.monthlyRent
                        : null;

                    return (
                      <tr
                        key={property.id}
                        className="click-row"
                        onClick={() => navigate(`/real-estate/${property.id}`)}
                      >
                        <td>
                          <div className="companyCell">
                            {property.websiteUrl ? (
                              <Favicon
                                websiteUrl={property.websiteUrl}
                                size={20}
                                alt=""
                                className="companyFavicon"
                              />
                            ) : (
                              <span
                                className="companyFavicon"
                                style={{
                                  display: 'inline-block',
                                  width: 20,
                                  height: 20,
                                  borderRadius: 6,
                                  background: 'var(--global-separator-color)',
                                }}
                                aria-hidden
                              />
                            )}
                            <span>{property.name}</span>
                          </div>
                        </td>
                        <td>{realEstateTypeLabel(property.propertyType)}</td>
                        <td className="muted">{property.address?.trim() || '—'}</td>
                        <td>{formatEuro(property.currentValue, true)}</td>
                        <td>
                          <span className={equityClassName(equity)}>{formatEuro(equity, true)}</span>
                        </td>
                        <td>{mortgage != null ? formatEuro(mortgage, true) : '—'}</td>
                        <td>{rent != null ? formatEuro(rent, true) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <GlobalEmptyState
            buttonText="Add property"
            onButtonClick={openAddSidebar}
            buttonIcon={plusIcon}
          />
        )}
      </section>
    </>
  );

  const sections: ViewSection[] = [
    ...(summarySection ? [{ id: 'summary', label: 'Summary', content: summarySection }] : []),
    { id: 'properties', label: 'Properties', content: propertiesSection },
  ];

  return (
    <ViewPage title="Real estate" actions={toolbarActions}>
      <ViewSections mode={layoutMode} sections={sections} />

      <GlobalSidebar isOpen={addSidebarOpen} onClose={closeAddSidebar}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Add property</h2>
          <p className="bankProviderConnectIntro">
            Track a real-estate holding with its market value, mortgage, and rental income. Net
            equity flows into your net worth.
          </p>
          <RealEstatePropertyForm
            saving={saving}
            error={saveError}
            onSubmit={handleSave}
            onCancel={closeAddSidebar}
          />
        </div>
      </GlobalSidebar>
    </ViewPage>
  );
}
